import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "./AuthContext";
import { useAgencySession } from "@/features/agency/AgencySessionContext";

export interface Organization {
  id: string;
  name: string;
  slug: string;
}

export interface OrganizationMembership {
  organization_id: string;
  role: "owner" | "admin" | "member";
  organization: Organization;
}

interface OrganizationMembershipQueryRow {
  organization_id: string;
  role: string;
  organization: Organization | Organization[] | null;
}

function isOrganizationRole(role: string): role is OrganizationMembership["role"] {
  return role === "owner" || role === "admin" || role === "member";
}

interface OrganizationContextValue {
  /** All orgs the current user is a member of (cross-product, e.g., a Forge church + Relay agency). */
  memberships: OrganizationMembership[];
  /** The active org for this session — operated client during agency sessions, otherwise the user-selected (or first) membership. */
  organization: Organization | null;
  organizationId: string | null;
  organizationRole: OrganizationMembership["role"] | null;
  isLoading: boolean;
  error: Error | null;
  /** The persisted active org id — null during agency sessions (which override org selection). */
  activeOrgId: string | null;
  /** Switch the active org and persist the choice to localStorage. No-op during agency sessions. */
  setActiveOrgId: (id: string) => void;
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined);

/**
 * Fetches the user's organization memberships from the SHARED `public` schema
 * (Relay and Forge share auth.users + public.organizations + public.organization_members).
 *
 * Critically, this filters to orgs that are ENROLLED IN RELAY via
 * public.product_enrollments. Without this filter, a user who's a member
 * of a Forge church (e.g. for kids check-in) would see that church show up
 * inside the Relay app — which is conceptually wrong: that church has no
 * Relay relationship.
 *
 * The relay client defaults to the `relay` schema, so we explicitly switch
 * via `.schema("public")` to make the cross-product read obvious.
 *
 * Implementation: two parallel queries (memberships, relay enrollments) +
 * intersect in JS. Cleaner than a multi-table inner join in PostgREST and
 * avoids any cardinality / aliasing footguns in the relational-select syntax.
 */
async function fetchMemberships(userId: string): Promise<OrganizationMembership[]> {
  const publicClient = supabase.schema("public");

  const [membershipResult, enrollmentResult] = await Promise.all([
    publicClient
      .from("organization_members")
      .select(
        `
          organization_id,
          role,
          organization:organizations ( id, name, slug )
        `
      )
      .eq("user_id", userId)
      .order("created_at", { ascending: true })
      .order("organization_id", { ascending: true }),
    publicClient
      .from("product_enrollments")
      .select("organization_id")
      .eq("product", "relay")
      .in("status", ["trial", "active"]),
  ]);

  if (membershipResult.error) throw membershipResult.error;
  if (enrollmentResult.error) throw enrollmentResult.error;

  const memberships = (membershipResult.data ?? [])
    .map((row: OrganizationMembershipQueryRow): OrganizationMembership | null => {
      // Supabase's relational select returns `organization` as object OR
      // array depending on relationship cardinality — normalize to object.
      const organization = Array.isArray(row.organization) ? row.organization[0] : row.organization;

      if (!organization || !isOrganizationRole(row.role)) return null;

      return {
        organization_id: row.organization_id,
        role: row.role,
        organization,
      };
    })
    .filter((membership): membership is OrganizationMembership => membership !== null);

  const relayEnrollments = enrollmentResult.data ?? [];

  // Backward-compatible fallback for environments that have legacy org memberships
  // but have not been backfilled into public.product_enrollments yet.
  // If no relay enrollments exist at all, preserve pre-enrollment behavior so users
  // are not logged in without an active organization.
  if (relayEnrollments.length === 0) {
    return memberships;
  }

  const relayOrgIds = new Set(
    relayEnrollments.map((row: { organization_id: string }) => row.organization_id)
  );

  return memberships.filter((membership) => relayOrgIds.has(membership.organization_id));
}

const STORAGE_KEY = "relay-active-org-id";

function readStoredActiveOrgId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredActiveOrgId(id: string) {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* Storage can be blocked; keep the in-memory selection. */
  }
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { clientOrgId, clientOrgName, clientOrgSlug } = useAgencySession();
  const queryClient = useQueryClient();

  // Persisted active-org selection — reads from localStorage on mount when available.
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(readStoredActiveOrgId);

  const setActiveOrgId = useCallback((id: string) => {
    // Agency sessions scope the app to the operated client org and should not
    // mutate the user's persisted cross-org selection.
    if (clientOrgId || id === activeOrgId) return;

    writeStoredActiveOrgId(id);
    setActiveOrgIdState(id);

    // Many Relay queries are org-scoped in their queryFn but predate the
    // multi-org switcher and do not all include organizationId in the cache key.
    // Clear the app cache on org switches so stale data from the previous org
    // cannot remain visible or be edited under the newly selected org.
    queryClient.clear();
  }, [activeOrgId, clientOrgId, queryClient]);

  const query = useQuery({
    queryKey: ["organization-memberships", user?.id],
    queryFn: () => fetchMemberships(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const memberships = useMemo(() => query.data ?? [], [query.data]);

  // Agency session overrides org selection entirely — the operated client is
  // always the active org during a support/agency session.
  const sessionMembership = useMemo<OrganizationMembership | null>(() => {
    if (!clientOrgId || !clientOrgName || !clientOrgSlug) return null;

    return {
      organization_id: clientOrgId,
      role: "admin",
      organization: {
        id: clientOrgId,
        name: clientOrgName,
        slug: clientOrgSlug,
      },
    };
  }, [clientOrgId, clientOrgName, clientOrgSlug]);

  // When no agency session is active, prefer the stored org if it's still a
  // valid membership; fall back to the first membership.
  const activeMembership = useMemo(() => {
    if (sessionMembership) return sessionMembership;
    if (!memberships.length) return null;
    const stored = activeOrgId
      ? memberships.find((m) => m.organization_id === activeOrgId)
      : null;
    return stored ?? memberships[0];
  }, [sessionMembership, memberships, activeOrgId]);

  // Auto-select and persist the first membership when the stored value is
  // missing or no longer valid (e.g., user was removed from the org).
  useEffect(() => {
    if (!memberships.length) return;
    const valid =
      activeOrgId && memberships.some((m) => m.organization_id === activeOrgId);
    if (!valid && memberships[0]) {
      setActiveOrgId(memberships[0].organization_id);
    }
  }, [memberships, activeOrgId, setActiveOrgId]);

  const value = useMemo<OrganizationContextValue>(
    () => ({
      memberships,
      organization: activeMembership?.organization ?? null,
      organizationId: activeMembership?.organization_id ?? null,
      organizationRole: activeMembership?.role ?? null,
      isLoading: authLoading || (!!user?.id && query.isPending),
      error: (query.error as Error) ?? null,
      activeOrgId,
      setActiveOrgId,
    }),
    [
      memberships,
      activeMembership,
      authLoading,
      user?.id,
      query.isPending,
      query.error,
      activeOrgId,
      setActiveOrgId,
    ],
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}

export function useOrganization(): OrganizationContextValue {
  const ctx = useContext(OrganizationContext);
  if (!ctx) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return ctx;
}
